import { MonetaryAmount } from "../../currency/currencyTypes"
import { toCurrency, toDineroOrUndefined } from "../../currency/currencyLib"
import type { MoneyFactory } from "../../currency/useMoney"

export type MenuDisplayItem = Readonly<{
    id: string
    tag?: string
    name: string
    description?: string
    allergens: ReadonlyArray<string>
    price?: MonetaryAmount
    imageUrl?: string
}>

export type MenuDisplayData = Readonly<{
    title?: string
    logoUrl?: string
    menuQrUrl?: string
    items: ReadonlyArray<MenuDisplayItem>
}>

export type MenuDisplayPriceParts = Readonly<{
    whole: string
    separator: string
    cents: string
    currency: string
}>

/** Splits the platform-formatted price into the parts the signage bubble typography needs. */
export function menuDisplayPriceParts(
    price: MonetaryAmount | undefined,
    moneyFactory: MoneyFactory,
): MenuDisplayPriceParts | undefined {
    const dineroPrice = toDineroOrUndefined(price)
    if (!price || !dineroPrice) return undefined

    const formatted = moneyFactory.formatAsNumber(dineroPrice)
    const [, whole = formatted, separator = "", cents = ""] = formatted.match(/^(.+?)(\D)(\d+)$/) ?? []
    const currency = moneyFactory.getLocalizedCurrencySymbol(toCurrency(price.currency))
    return { whole, separator, cents, currency }
}

// Auto-advance interval for the featured dish (ms) and the slide transition duration (ms).
export const MENU_DISPLAY_SLIDE_INTERVAL_MS = 7000
export const MENU_DISPLAY_SLIDE_TRANSITION_MS = 1000

// The sidebar fits at most this many upcoming dishes above the QR block on a 16:9 stage.
export const MENU_DISPLAY_SIDEBAR_MAX_ITEMS = 3

// Signage fonts (loaded in public/index.html): Oswald for display titles, Inter for body/prices,
// Fuzzy Bubbles for the handwritten "Scan for week menu" call-out.
export const MENU_DISPLAY_DISPLAY_FONT = "'Oswald', sans-serif"
export const MENU_DISPLAY_BODY_FONT = "'Inter', sans-serif"
export const MENU_DISPLAY_SCRIPT_FONT = "'Fuzzy Bubbles', cursive"

// Exact brand colors of the canteen signage that have no equivalent in the app's design tokens.
// Slate greys/borders from the design come from theme.palette.tailwind.slate instead.
export const MENU_DISPLAY_COLORS = {
    mint: "#ADE2D8",
    darkGreen: "#1E5447",
    cream: "#FBFAF6",
} as const
